import React, { createContext, useState, useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import client from '../api/client';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const queryClient = useQueryClient();

    useEffect(() => {
        const checkAuth = async () => {
            try {
                // Check if we are in Telegram Mini App
                const tg = window.Telegram?.WebApp;

                if (tg) {
                    try {
                        // REQUIRED for Mini App to work and send data
                        if (typeof tg.ready === 'function') tg.ready();
                        if (typeof tg.expand === 'function') tg.expand();

                        // Sometimes initData is not available immediately
                        let attempts = 0;
                        while (!tg.initData && attempts < 20) { // Up to 1 second
                            await new Promise(r => setTimeout(r, 50));
                            attempts++;
                        }

                        if (tg.initData) {
                            const tgUser = tg.initDataUnsafe?.user;
                            if (tgUser) {
                                const res = await client.post('login-telegram', {
                                    telegram_id: String(tgUser.id),
                                    init_data: tg.initData
                                });
                                const { access_token, user: userData } = res.data;
                                localStorage.setItem('token', access_token);
                                setUser(userData);
                                return;
                            }
                        }
                    } catch (tgErr) {
                        console.error("Telegram initialization error:", tgErr);
                    }
                }

                // Check if we are in forced demo mode (refresh)
                if (localStorage.getItem('isDemoMode') === 'true') {
                    const user = { id: 1, name: 'Собственник (Demo)', role: 'admin', is_demo: true };
                    setUser(user);
                    return;
                }

                const token = localStorage.getItem('token');
                if (token) {
                    try {
                        const res = await client.get('me');
                        const userData = res.data;

                        if (userData.role === 'demo_viewer') {
                            localStorage.setItem('isDemoMode', 'true');
                            userData.is_demo = true;
                        }

                        if (userData.role !== 'demo_viewer' && localStorage.getItem('isDemoMode') === 'true') {
                            if (userData.role !== 'admin') {
                                localStorage.removeItem('isDemoMode');
                            }
                        }

                        setUser(userData);
                    } catch (error) {
                        console.error("Auth check failed:", error);
                        localStorage.removeItem('token');
                    }
                }
            } catch (globalErr) {
                console.error("Global auth error:", globalErr);
            } finally {
                setLoading(false);
            }
        };
        checkAuth();
    }, []);

    const login = async (username, password) => {
        const params = new URLSearchParams();
        params.append('username', username);
        params.append('password', password);

        const res = await client.post('login', params);

        if (res.data.require_2fa) {
            return res.data; // { require_2fa: true, user_id: 123 }
        }

        const { access_token, user } = res.data;

        localStorage.setItem('token', access_token);

        // CHECK ID USER IS A DEMO VIEWER
        if (user.role === 'demo_viewer') {
            localStorage.setItem('isDemoMode', 'true');
            user.is_demo = true;
        } else {
            localStorage.removeItem('isDemoMode');
        }

        setUser(user);
        return user;
    };

    const verify2fa = async (userId, code) => {
        const res = await client.post('verify-2fa', { user_id: userId, code });
        const { access_token, user } = res.data;

        localStorage.setItem('token', access_token);

        if (user.role === 'demo_viewer') {
            localStorage.setItem('isDemoMode', 'true');
            user.is_demo = true;
        } else {
            localStorage.removeItem('isDemoMode');
        }

        setUser(user);
        return user;
    };

    const loginDemo = async () => {
        // Clear all previous data to prevent leaks
        queryClient.clear();

        // Set demo mode flag
        localStorage.setItem('isDemoMode', 'true');

        // Ensure client interceptor picks it up (it reads from localStorage directly)

        // Mock login response
        const user = { id: 1, name: 'Собственник (Demo)', role: 'admin', is_demo: true };
        setUser(user);
        setLoading(false);
        return user;
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('isDemoMode');
        queryClient.clear(); // Clear cache on logout too
        setUser(null);
        window.location.href = '/login';
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, loginDemo, logout, verify2fa }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
